import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "wouter";
import {
  User as UserIcon,
  Phone,
  Wallet,
  MapPin,
  Loader2,
  CircleOff,
  Disc,
  Wind,
  Footprints,
  Trash2,
  ChevronRight,
} from "lucide-react";
import { useProfile, useUpdateProfile } from "@/hooks/use-auth";
import { useWalletBalance } from "@/hooks/use-wallet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";

type PrefKey =
  | "steeringWheelCover"
  | "airFreshener"
  | "floorCovers"
  | "trashBag"
  | "tissues";

interface PrefConfig {
  key: PrefKey;
  labelKey: string;
  Icon: React.ComponentType<{ className?: string }>;
}

const PREF_CONFIG: PrefConfig[] = [
  { key: "steeringWheelCover", labelKey: "profile.steeringWheelCover", Icon: Disc },
  { key: "airFreshener", labelKey: "profile.airFreshener", Icon: Wind },
  { key: "floorCovers", labelKey: "profile.floorCovers", Icon: Footprints },
  { key: "trashBag", labelKey: "profile.trashBag", Icon: Trash2 },
  { key: "tissues", labelKey: "profile.tissues", Icon: CircleOff },
];

export default function Profile() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const { toast } = useToast();

  const { data: user, isLoading } = useProfile();
  const { data: walletData } = useWalletBalance();
  const updateMutation = useUpdateProfile();

  const walletBalance = walletData?.balance ?? 0;

  const [name, setName] = useState("");
  const [preferences, setPreferences] = useState<Record<PrefKey, boolean>>({
    steeringWheelCover: true,
    airFreshener: true,
    floorCovers: true,
    trashBag: true,
    tissues: true,
  });

  // Sync local state once profile data is loaded.
  useEffect(() => {
    if (user) {
      setName(user.name || "");
      const prefs = user.preferences;
      setPreferences({
        steeringWheelCover: prefs?.steeringWheelCover ?? true,
        airFreshener: prefs?.airFreshener ?? true,
        floorCovers: prefs?.floorCovers ?? true,
        trashBag: prefs?.trashBag ?? true,
        tissues: prefs?.tissues ?? true,
      });
    }
  }, [user]);

  const displayName = user?.name || user?.phone || "";
  const initials = displayName
    ? displayName
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((part) => part[0])
        .join("")
        .toUpperCase()
    : "";

  const handleSaveName = async () => {
    try {
      // Save name AND preferences together (so first-time users get both)
      await updateMutation.mutateAsync({ name, preferences });
      toast({ title: t("profile.saved") });
    } catch (error: any) {
      toast({
        title: t("common.error"),
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleToggle = (key: PrefKey, value: boolean) => {
    setPreferences((prev) => ({ ...prev, [key]: value }));
  };

  const handleSavePreferences = async () => {
    try {
      await updateMutation.mutateAsync({ preferences });
      toast({ title: t("profile.saved") });
    } catch (error: any) {
      toast({
        title: t("common.error"),
        description: error.message,
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-24 bg-background" dir={isRtl ? "rtl" : "ltr"}>
      <div className="container mx-auto px-4 max-w-2xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          {isLoading ? (
            <Skeleton className="w-20 h-20 rounded-full" />
          ) : (
            <div className="w-20 h-20 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-2xl font-bold flex-shrink-0">
              {initials || <UserIcon className="w-9 h-9" />}
            </div>
          )}
          <div className="min-w-0">
            <h1 className="text-2xl font-bold truncate">
              {isLoading ? <Skeleton className="h-7 w-40" /> : (user?.name || user?.phone || t("profile.title"))}
            </h1>
            {!isLoading && (
              <p className="text-muted-foreground text-sm flex items-center gap-1 mt-1">
                <UserIcon className="w-3.5 h-3.5" />
                {t("profile.title")}
              </p>
            )}
          </div>
        </div>

        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-32 w-full rounded-2xl" />
            <Skeleton className="h-48 w-full rounded-2xl" />
            <Skeleton className="h-72 w-full rounded-2xl" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Editable name */}
            <div className="bg-card rounded-2xl border border-border/50 p-5">
              <Label htmlFor="profile-name" className="text-sm font-semibold mb-2 block">
                {t("profile.editName")}
              </Label>
              <div className="flex gap-2">
                <Input
                  id="profile-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className={isRtl ? "text-right" : ""}
                  dir={isRtl ? "rtl" : "ltr"}
                  data-testid="input-profile-name"
                />
                <Button
                  onClick={handleSaveName}
                  disabled={updateMutation.isPending || !name.trim()}
                  data-testid="button-save-name"
                >
                  {updateMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    t("profile.save")
                  )}
                </Button>
              </div>
            </div>

            {/* Phone (read-only) */}
            <div className="bg-card rounded-2xl border border-border/50 p-5 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                <Phone className="w-5 h-5 text-muted-foreground" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">{t("profile.phone")}</p>
                <p className="font-medium truncate" dir="ltr">{user?.phone}</p>
              </div>
            </div>

            {/* Wallet card */}
            <Link href="/wallet" data-testid="link-profile-wallet">
              <div className="bg-card rounded-2xl border border-border/50 p-5 flex items-center gap-3 hover:border-primary/50 transition-colors cursor-pointer">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Wallet className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground">{t("profile.walletBalance")}</p>
                  <p className="font-semibold">
                    {walletBalance.toFixed(3)} {t("services.currency")}
                  </p>
                </div>
                <ChevronRight className={`w-5 h-5 text-muted-foreground ${isRtl ? "rotate-180" : ""}`} />
              </div>
            </Link>

            {/* My Addresses */}
            <Link href="/addresses" data-testid="link-profile-addresses">
              <div className="bg-card rounded-2xl border border-border/50 p-5 flex items-center gap-3 hover:border-primary/50 transition-colors cursor-pointer">
                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium">{t("profile.myAddresses")}</p>
                </div>
                <ChevronRight className={`w-5 h-5 text-muted-foreground ${isRtl ? "rotate-180" : ""}`} />
              </div>
            </Link>

            {/* Preferences section */}
            <div className="bg-card rounded-2xl border border-border/50 p-5">
              <h2 className="font-semibold text-lg mb-1 flex items-center gap-2">
                <CircleOff className="w-5 h-5 text-primary" />
                {t("profile.preferences")}
              </h2>
              <p className="text-sm text-muted-foreground mb-4">
                {t("profile.preferencesDesc")}
              </p>

              <div className="space-y-1">
                {PREF_CONFIG.map(({ key, labelKey, Icon }) => (
                  <div
                    key={key}
                    className="flex items-center justify-between py-3 border-b border-border/40 last:border-0"
                  >
                    <Label
                      htmlFor={`pref-${key}`}
                      className="flex items-center gap-3 cursor-pointer text-sm font-medium"
                    >
                      <Icon className="w-5 h-5 text-muted-foreground" />
                      {t(labelKey)}
                    </Label>
                    <Switch
                      id={`pref-${key}`}
                      checked={preferences[key]}
                      onCheckedChange={(value) => handleToggle(key, value)}
                      data-testid={`switch-pref-${key}`}
                    />
                  </div>
                ))}
              </div>

              <Button
                onClick={handleSavePreferences}
                disabled={updateMutation.isPending}
                className="w-full mt-4"
                data-testid="button-save-preferences"
              >
                {updateMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  t("profile.save")
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
